/**
 * AdminShell — master layout wrapper for Command Center & Admin pages.
 *
 * Visual language: Swiss / High-Contrast (white + red + black)
 * Per /app/design_guidelines.json
 *
 *   Desktop:  collapsible left nav rail (icon + label, collapses to icon-only)
 *   Mobile:   bottom tab bar with 4 primary destinations + "More" sheet
 *   Header:   sticky glassmorphism w/ search (Cmd-K stub) and quick actions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, CalendarDays, Wallet, Users, MoreHorizontal, Search,
  Plus, Calculator, Navigation, Save, ChevronsLeft, ChevronsRight, X,
  ArrowLeft, LogOut,
} from 'lucide-react';

// ── Consolidated Information Architecture (from design_guidelines.json) ──
export const NAV_BUCKETS = [
  {
    id: 'command',
    label: 'Command',
    icon: Activity,
    tabs: [
      { id: 'ops',       label: 'Overview' },
      { id: 'dailyops',  label: 'Daily Ops' },
      { id: 'opslogs',   label: 'Ops Logs' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: CalendarDays,
    tabs: [
      { id: 'quotebookings', label: 'Bookings & Quotes' },
      { id: 'contracts',     label: 'Contracts & Invoices' },
    ],
  },
  {
    id: 'financials',
    label: 'Financials',
    icon: Wallet,
    tabs: [
      { id: 'weeklypl',   label: 'Weekly & P&L' },
      { id: 'monthlypl',  label: 'Monthly P&L' },
    ],
  },
  {
    id: 'network',
    label: 'Network',
    icon: Users,
    tabs: [
      { id: 'teampay',  label: 'Team & Pay' },
      { id: 'fulfillment', label: 'Fulfillment' },
      { id: 'clients',  label: 'Clients' },
      { id: 'quo',      label: 'Quo' },
    ],
  },
];

// Map any tab id → its bucket id (for the mobile bottom-bar highlight)
export const TAB_TO_BUCKET = (() => {
  const m = {};
  NAV_BUCKETS.forEach(b => b.tabs.forEach(t => { m[t.id] = b.id; }));
  return m;
})();

// ── Quick action items (rendered in header) ──
export const buildQuickActions = ({ onQuickAdd, onQuickQuote, onLiveTrack, onSnapshot, GoogleCalendarButton, snapshotRunning }) => ([
  { id: 'quick-add',     label: 'Quick Add',    icon: Plus,        onClick: onQuickAdd,     primary: true },
  { id: 'quick-quote',   label: 'Quote',        icon: Calculator,  onClick: onQuickQuote },
  { id: 'live-track',    label: 'Live Track',   icon: Navigation,  onClick: onLiveTrack },
  { id: 'gcal',          label: 'Calendar',     custom: GoogleCalendarButton },
  { id: 'snapshot',      label: 'Snapshot',     icon: Save,        onClick: onSnapshot, busy: snapshotRunning },
]);

// ─────────────────────────────────────────────────────────────────────────────
// Shell
// ─────────────────────────────────────────────────────────────────────────────
export default function AdminShell({
  activeTab,
  onTabChange,
  title = 'ACE Command Center',
  subtitle = 'Dispatch · Fleet · CRM',
  quickActions = [],
  children,
}) {
  const navigate = useNavigate();
  const [railOpen, setRailOpen] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  // Persist rail state
  useEffect(() => {
    const saved = localStorage.getItem('admin_rail_open');
    if (saved !== null) setRailOpen(saved === '1');
  }, []);
  useEffect(() => { localStorage.setItem('admin_rail_open', railOpen ? '1' : '0'); }, [railOpen]);

  // Cmd-K
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(o => !o);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const activeBucketId = TAB_TO_BUCKET[activeTab] || 'command';

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#0A0A0A] font-sans">
      {/* ───── DESKTOP NAV RAIL ───── */}
      <aside
        className={`hidden md:flex fixed inset-y-0 left-0 z-40 flex-col bg-white border-r border-[#E5E5E5] transition-[width] duration-200 ease-out ${railOpen ? 'w-56' : 'w-16'}`}
        data-testid="admin-rail"
      >
        <div className={`flex items-center ${railOpen ? 'justify-between px-4' : 'justify-center px-0'} h-14 border-b border-[#E5E5E5]`}>
          {railOpen ? (
            <>
              <button onClick={() => navigate('/admin')} className="flex items-center gap-2 group" data-testid="rail-home">
                <div className="h-8 w-8 bg-[#0A0A0A] text-white flex items-center justify-center font-black text-sm">A</div>
                <div>
                  <div className="text-xs font-black tracking-tight">ACE</div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#666]">Command</div>
                </div>
              </button>
              <button onClick={() => setRailOpen(false)} className="text-[#666] hover:text-[#0A0A0A]" data-testid="rail-collapse">
                <ChevronsLeft className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button onClick={() => setRailOpen(true)} className="text-[#666] hover:text-[#0A0A0A]" data-testid="rail-expand">
              <ChevronsRight className="h-4 w-4" />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {NAV_BUCKETS.map(b => {
            const Icon = b.icon;
            const isActiveBucket = activeBucketId === b.id;
            return (
              <div key={b.id} className="mb-1">
                <button
                  onClick={() => onTabChange(b.tabs[0].id)}
                  className={`w-full flex items-center ${railOpen ? 'gap-3 px-4 justify-start' : 'justify-center px-0'} h-10 text-sm font-semibold transition-colors ${isActiveBucket ? 'bg-[#0A0A0A] text-white' : 'text-[#0A0A0A] hover:bg-[#F9F9FA]'}`}
                  data-testid={`rail-bucket-${b.id}`}
                  title={!railOpen ? b.label : undefined}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                  {railOpen && <span>{b.label}</span>}
                </button>
                {railOpen && isActiveBucket && (
                  <div className="ml-9 mt-1 mb-2 border-l border-[#E5E5E5]">
                    {b.tabs.map(t => (
                      <button
                        key={t.id}
                        onClick={() => onTabChange(t.id)}
                        className={`block w-full text-left pl-3 pr-2 py-1.5 text-xs transition-colors ${activeTab === t.id ? 'text-[#E52B20] font-bold border-l-2 border-[#E52B20] -ml-px' : 'text-[#666] hover:text-[#0A0A0A]'}`}
                        data-testid={`rail-tab-${t.id}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-[#E5E5E5] p-3">
          <button
            onClick={() => navigate('/')}
            className={`flex items-center ${railOpen ? 'gap-2' : 'justify-center w-full'} text-[10px] uppercase tracking-[0.2em] text-[#666] hover:text-[#0A0A0A] font-bold`}
            data-testid="rail-exit"
          >
            <LogOut className="h-3 w-3" />
            {railOpen && 'Exit'}
          </button>
        </div>
      </aside>

      {/* ───── MAIN CONTENT WRAPPER ───── */}
      <div className={`transition-[padding] duration-200 ${railOpen ? 'md:pl-56' : 'md:pl-16'} pb-20 md:pb-0`}>
        {/* ───── HEADER ───── */}
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-[#E5E5E5]" data-testid="admin-header">
          <div className="px-4 md:px-6 h-14 flex items-center gap-3 justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {/* Mobile back to admin */}
              <button onClick={() => navigate('/admin')} className="md:hidden text-[#666] hover:text-[#0A0A0A]" data-testid="header-back">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-sm md:text-base font-black tracking-tight truncate">{title}</h1>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] hidden md:block">{subtitle}</p>
              </div>
            </div>

            {/* Search trigger (Cmd-K) */}
            <button
              onClick={() => setCmdOpen(true)}
              className="hidden md:flex items-center gap-2 h-9 px-3 text-xs text-[#666] bg-[#F9F9FA] border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors min-w-[280px]"
              data-testid="cmd-k-trigger"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">Search anything…</span>
              <kbd className="font-mono text-[10px] text-[#666] px-1.5 py-0.5 border border-[#E5E5E5]">⌘K</kbd>
            </button>

            {/* Quick actions */}
            <div className="flex items-center gap-1">
              <button onClick={() => setCmdOpen(true)} className="md:hidden h-9 w-9 flex items-center justify-center text-[#0A0A0A] hover:bg-[#F9F9FA]" data-testid="cmd-k-trigger-mobile">
                <Search className="h-4 w-4" />
              </button>
              {quickActions.map(qa => {
                if (qa.custom) {
                  return <div key={qa.id} className="hidden md:block">{qa.custom}</div>;
                }
                const Icon = qa.icon;
                return (
                  <button
                    key={qa.id}
                    onClick={qa.onClick}
                    disabled={qa.busy}
                    className={`h-9 ${qa.primary ? 'md:px-3 px-2 bg-[#E52B20] text-white hover:bg-[#C92218]' : 'w-9 text-[#0A0A0A] hover:bg-[#F9F9FA] border border-transparent hover:border-[#E5E5E5]'} flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50`}
                    title={qa.label}
                    data-testid={`qa-${qa.id}`}
                  >
                    <Icon className={`h-4 w-4 ${qa.busy ? 'animate-pulse' : ''}`} />
                    {qa.primary && <span className="hidden md:inline">{qa.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sub-tab strip — only visible when current bucket has more than one tab */}
          <SubTabStrip activeBucketId={activeBucketId} activeTab={activeTab} onTabChange={onTabChange} />
        </header>

        {/* ───── PAGE BODY ───── */}
        <main className="px-4 md:px-6 py-4 md:py-6 max-w-[1600px] mx-auto" data-testid="admin-content">
          {children}
        </main>
      </div>

      {/* ───── MOBILE BOTTOM TAB BAR ───── */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5E5E5]" data-testid="mobile-bottom-tabs">
        <div className="grid grid-cols-5 h-16">
          {NAV_BUCKETS.map(b => {
            const Icon = b.icon;
            const isActive = activeBucketId === b.id;
            return (
              <button
                key={b.id}
                onClick={() => onTabChange(b.tabs[0].id)}
                className={`flex flex-col items-center justify-center gap-1 transition-colors ${isActive ? 'text-[#E52B20]' : 'text-[#666]'}`}
                data-testid={`bottom-tab-${b.id}`}
              >
                <Icon className="h-5 w-5" strokeWidth={2.25} />
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold">{b.label}</span>
                {isActive && <span className="absolute top-0 h-0.5 w-8 bg-[#E52B20]" />}
              </button>
            );
          })}
          <button
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center gap-1 text-[#666]"
            data-testid="bottom-tab-more"
          >
            <MoreHorizontal className="h-5 w-5" strokeWidth={2.25} />
            <span className="text-[9px] uppercase tracking-[0.15em] font-bold">More</span>
          </button>
        </div>
      </nav>

      {/* ───── MOBILE "MORE" SHEET ───── */}
      {moreOpen && (
        <MobileMoreSheet
          quickActions={quickActions}
          onClose={() => setMoreOpen(false)}
          onTabChange={(t) => { onTabChange(t); setMoreOpen(false); }}
          activeTab={activeTab}
        />
      )}

      {/* ───── CMD PALETTE STUB ───── */}
      {cmdOpen && <CommandPalette onClose={() => setCmdOpen(false)} onTabChange={onTabChange} />}
    </div>
  );
}

// ── Sub-tab strip (under header, when bucket has >1 tab) ──
function SubTabStrip({ activeBucketId, activeTab, onTabChange }) {
  const bucket = NAV_BUCKETS.find(b => b.id === activeBucketId);
  if (!bucket || bucket.tabs.length <= 1) return null;
  return (
    <div className="border-t border-[#E5E5E5] bg-white">
      <div className="px-4 md:px-6 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max h-11">
          {bucket.tabs.map(t => (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              className={`h-9 px-3 text-xs font-bold uppercase tracking-wider transition-colors border-b-2 -mb-px ${activeTab === t.id ? 'border-[#E52B20] text-[#0A0A0A]' : 'border-transparent text-[#666] hover:text-[#0A0A0A]'}`}
              data-testid={`subtab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Mobile "More" Sheet ──
function MobileMoreSheet({ quickActions, onClose, onTabChange, activeTab }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-end md:items-center justify-center" onClick={onClose} data-testid="more-sheet">
      <div className="bg-white border-t border-[#0A0A0A] w-full md:max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-black uppercase tracking-wider">More</h3>
          <button onClick={onClose} className="text-[#666] hover:text-[#0A0A0A]"><X className="h-5 w-5" /></button>
        </div>

        {/* All sub-tabs */}
        <div className="p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold mb-2">Navigate</p>
          <div className="grid grid-cols-2 gap-2">
            {NAV_BUCKETS.flatMap(b => b.tabs.map(t => ({ ...t, bucket: b.label }))).map(t => (
              <button
                key={t.id}
                onClick={() => onTabChange(t.id)}
                className={`text-left p-3 border ${activeTab === t.id ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E5E5E5] hover:border-[#0A0A0A]'}`}
                data-testid={`more-tab-${t.id}`}
              >
                <p className="text-[9px] uppercase tracking-[0.2em] opacity-70">{t.bucket}</p>
                <p className="text-sm font-bold mt-0.5">{t.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div className="p-4 border-t border-[#E5E5E5]">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold mb-2">Quick Actions</p>
          <div className="grid grid-cols-2 gap-2">
            {quickActions.filter(qa => !qa.custom).map(qa => {
              const Icon = qa.icon;
              return (
                <button
                  key={qa.id}
                  onClick={() => { qa.onClick?.(); onClose(); }}
                  className={`flex items-center gap-2 p-3 border ${qa.primary ? 'border-[#E52B20] bg-[#E52B20] text-white' : 'border-[#E5E5E5] text-[#0A0A0A] hover:border-[#0A0A0A]'} text-xs font-bold uppercase tracking-wider`}
                  data-testid={`more-action-${qa.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {qa.label}
                </button>
              );
            })}
            {quickActions.filter(qa => qa.custom).map(qa => (
              <div key={qa.id} className="contents">{qa.custom}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Cmd-K palette ──
function CommandPalette({ onClose, onTabChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  // Live search w/ debounce
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/admin/search?q=${encodeURIComponent(q)}&limit=15`);
        const d = await r.json();
        setResults(d.results || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q, API]);

  // Quick-nav items (always shown when q is empty)
  const quickNav = [
    ...NAV_BUCKETS.flatMap(b => b.tabs.map(t => ({
      type: 'nav', label: t.label, hint: b.label,
      action: () => { onTabChange(t.id); onClose(); },
    }))),
    { type: 'nav', label: 'Live Tracking', hint: 'External', action: () => { navigate('/admin/tracking'); onClose(); } },
    { type: 'nav', label: 'Staff Portal', hint: 'External', action: () => { window.open('/staff', '_blank'); onClose(); } },
    { type: 'nav', label: 'Public Site', hint: 'External', action: () => { window.open('/', '_blank'); onClose(); } },
  ];

  const navigateToResult = (item) => {
    const dl = item.deep_link || {};
    if (!dl.route) { onClose(); return; }
    if (dl.route.startsWith('http') || dl.route.startsWith('/partners') || dl.route === '/staff' || dl.route === '/admin/tracking') {
      window.open(dl.route, '_blank');
    } else if (dl.route === '/command-center' && dl.tab) {
      onTabChange(dl.tab);
    } else {
      navigate(dl.route);
    }
    onClose();
  };

  const TYPE_BADGES = {
    booking: { label: 'Booking', cls: 'bg-[#0A0A0A] text-white' },
    client:  { label: 'Client',  cls: 'bg-[#E52B20] text-white' },
    vehicle: { label: 'Vehicle', cls: 'bg-[#039855] text-white' },
    partner: { label: 'Partner', cls: 'bg-[#F79009] text-white' },
    rental:  { label: 'Rental',  cls: 'bg-[#0A0A0A] text-white' },
    condition_report: { label: 'Report', cls: 'bg-white text-[#0A0A0A] border border-[#0A0A0A]' },
    command: { label: 'Jump', cls: 'bg-white text-[#E52B20] border border-[#E52B20]' },
  };

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});
  const typeOrder = ['command', 'booking', 'client', 'vehicle', 'partner', 'rental', 'condition_report'];

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center pt-[10vh]" onClick={onClose} data-testid="cmd-palette">
      <div className="bg-white border border-[#0A0A0A] w-full max-w-2xl mx-4 shadow-[4px_4px_0px_#0A0A0A]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-[#E5E5E5]">
          <Search className="h-4 w-4 text-[#666]" />
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search bookings, clients, vehicles, partners… or jump to a tab"
            className="flex-1 outline-none text-sm font-medium"
            data-testid="cmd-input"
          />
          {loading && <Loader2Icon />}
          <kbd className="font-mono text-[10px] text-[#666] px-1.5 py-0.5 border border-[#E5E5E5]">ESC</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <div>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold">Jump to</div>
              {quickNav.slice(0, 12).map((it, i) => (
                <button
                  key={`qn-${i}`}
                  onClick={it.action}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[#F9F9FA] border-b border-[#E5E5E5] last:border-b-0"
                  data-testid={`cmd-quicknav-${i}`}
                >
                  <span className="text-sm font-semibold">{it.label}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#666]">{it.hint}</span>
                </button>
              ))}
            </div>
          ) : results.length === 0 && !loading ? (
            <p className="text-center text-sm text-[#666] py-8">No matches for &ldquo;{q}&rdquo;</p>
          ) : (
            <div>
              {typeOrder.filter(t => grouped[t]).map(type => (
                <div key={type}>
                  <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold">
                    {TYPE_BADGES[type]?.label || type}s · {grouped[type].length}
                  </div>
                  {grouped[type].map((it, i) => {
                    const badge = TYPE_BADGES[it.type];
                    return (
                      <button
                        key={`${type}-${it.id || i}`}
                        onClick={() => navigateToResult(it)}
                        className="w-full flex items-start justify-between gap-3 px-4 py-2.5 text-left hover:bg-[#F9F9FA] border-b border-[#E5E5E5] last:border-b-0"
                        data-testid={`cmd-result-${it.type}-${it.id || i}`}
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          {badge && (
                            <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 shrink-0 mt-0.5 ${badge.cls}`}>{badge.label}</span>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-bold truncate">{it.label}</p>
                            <p className="text-[11px] text-[#666] truncate">{it.hint}</p>
                          </div>
                        </div>
                        {it.status && (
                          <span className="text-[10px] uppercase tracking-widest text-[#666] font-bold shrink-0">{it.status}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-2 border-t border-[#E5E5E5] text-[10px] uppercase tracking-[0.15em] text-[#666] flex items-center justify-between">
          <span>Live search across all records</span>
          <span><kbd className="font-mono px-1 border border-[#E5E5E5]">↑</kbd><kbd className="font-mono px-1 border border-[#E5E5E5] ml-1">↓</kbd> navigate</span>
        </div>
      </div>
    </div>
  );
}

// Tiny inline loader (avoids adding another lucide import)
const Loader2Icon = () => (
  <svg className="h-4 w-4 animate-spin text-[#E52B20]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12a9 9 0 11-6.219-8.56" strokeLinecap="round" />
  </svg>
);
